import {Box, Collapse, Link, TableBody, TableCell, TableHead, TableRow, Typography} from "@mui/material";
import {FilterAltOutlined} from "@mui/icons-material";
import React from "react";
import {RiskReportTypes} from "../../types";
import palette from "../../theme/palette";
import {ReportTable} from "./report-table";

// Plain-language reason for each code node-api sends (chargeClassification FILTER_REASON).
export const FILTER_REASON_LABELS: Record<RiskReportTypes.FilterReason, string> = {
    below_level: 'Below your sensitivity level',
    dui: 'Excluded type: DUI / impaired driving',
    marijuana: 'Excluded type: marijuana',
    probation: 'Excluded type: probation violation',
    traffic: 'Traffic infraction',
    municipal: 'Municipal / ordinance violation',
    dismissed: 'Case dismissed',
    acquitted: 'Acquitted / not guilty',
    diversion: 'Completed diversion program',
    vacated: 'Conviction vacated or set aside',
};

export const filterReasonLabel = (reason: string) =>
    FILTER_REASON_LABELS[reason as RiskReportTypes.FilterReason] ?? 'Filtered by screening settings';

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

const DASH = '—';

interface Props {
    filteredCharges: RiskReportTypes.FilteredCharges;
}

// Safety Hub "N charges filtered out · View" (ClickUp: IRA filtered-charges reveal).
// Orgs check IRA results against court websites and report charges "we missed"
// that their own screening settings hid. This lists what was filtered out of the
// subject's own records, closed by default and clearly outside the assessment.
// Never rendered in the PDF (the caller only shows it in the live view).
export const FilteredCharges: React.FC<Props> = ({filteredCharges}) => {
    const [open, setOpen] = React.useState(false);
    // The payload is stored with saved reports, so tolerate a malformed one
    // rather than break the Criminal Records card.
    const total = Number(filteredCharges?.total) || 0;
    const listedTotal = Number(filteredCharges?.listedTotal) || 0;
    const trafficCount = Number(filteredCharges?.trafficCount) || 0;
    const cases = (Array.isArray(filteredCharges?.cases) ? filteredCharges.cases : [])
        .map((filteredCase) => ({...filteredCase, charges: Array.isArray(filteredCase?.charges) ? filteredCase.charges : []}))
        .filter((filteredCase) => filteredCase.charges.length > 0);
    if (!total) return null;

    const listed = cases.reduce((count, filteredCase) => count + filteredCase.charges.length, 0);
    const showState = new Set(cases.map((filteredCase) => filteredCase.state)).size > 1;

    return (
        <Box sx={{mt: 2}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                <FilterAltOutlined sx={{fontSize: 18, color: 'text.secondary'}}/>
                <Typography variant="body2" color="text.secondary">
                    {`${plural(total, 'charge')} filtered out by your organization's screening settings`}
                </Typography>
                <Link
                    component="button"
                    type="button"
                    variant="body2"
                    underline="hover"
                    fontWeight={600}
                    aria-expanded={open}
                    onClick={() => setOpen(!open)}
                    sx={{color: palette.darkBlue}}
                >
                    {open ? 'Hide' : 'View'}
                </Link>
            </Box>

            <Collapse in={open} unmountOnExit>
                <Box sx={{mt: 1.5, p: 2, bgcolor: '#F9FAFB', border: '1px solid #E4E8EC', borderRadius: 2}}>
                    <Typography variant="body2" color="text.secondary" sx={{mb: listed ? 1.5 : 0}}>
                        Not part of this assessment. Shown so you can compare it with court websites.
                        {listedTotal > listed ? ` Showing the ${listed} most recent of ${listedTotal}.` : ''}
                    </Typography>

                    {listed > 0 && (
                        <ReportTable aria-label="Filtered charges">
                            <TableHead>
                                <TableRow>
                                    {showState && <TableCell>State</TableCell>}
                                    <TableCell>Docket</TableCell>
                                    <TableCell>Court</TableCell>
                                    <TableCell>Disposition</TableCell>
                                    <TableCell sx={{width: '34%'}}>Charge</TableCell>
                                    <TableCell>Offense Date</TableCell>
                                    <TableCell>Why it was filtered</TableCell>
                                </TableRow>
                            </TableHead>
                            {cases.map((filteredCase, caseIndex) => (
                                <TableBody key={`${filteredCase.docket}-${caseIndex}`}>
                                    {filteredCase.charges.map((charge, chargeIndex) => (
                                        <TableRow key={chargeIndex}>
                                            {chargeIndex === 0 && (
                                                <>
                                                    {showState && <TableCell rowSpan={filteredCase.charges.length}>{filteredCase.state}</TableCell>}
                                                    <TableCell rowSpan={filteredCase.charges.length} sx={{whiteSpace: 'nowrap'}}>{filteredCase.docket || DASH}</TableCell>
                                                    <TableCell rowSpan={filteredCase.charges.length}>{filteredCase.court || DASH}</TableCell>
                                                    <TableCell rowSpan={filteredCase.charges.length}>{filteredCase.disposition || DASH}</TableCell>
                                                </>
                                            )}
                                            <TableCell>{charge.description || DASH}</TableCell>
                                            <TableCell sx={{whiteSpace: 'nowrap'}}>{charge.offenseDate || DASH}</TableCell>
                                            <TableCell>{filterReasonLabel(charge.reason)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            ))}
                        </ReportTable>
                    )}

                    {trafficCount > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{mt: listed ? 1.5 : 0.5}}>
                            {`+ ${plural(trafficCount, 'traffic infraction')}, not listed`}
                        </Typography>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};
